import { useState } from "react";
import { api } from "../../lib/api";
import { navigate } from "../../lib/route";

interface CmsTopBarProps {
  pageTitle?: string;
  portfolioUrl?: string;
  onToggleMobile?: () => void;
}

export function CmsTopBar({ pageTitle, portfolioUrl, onToggleMobile }: CmsTopBarProps) {
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishedOk, setPublishedOk] = useState(false);

  async function handlePublish() {
    setPublishing(true);
    setPublishError(null);
    setPublishedOk(false);
    try {
      // Load current portfolio to carry over all required fields.
      const portfolio = await api.getMyPortfolio();
      await api.updateMyPortfolio({
        display_name: portfolio.DisplayName,
        tagline: portfolio.Tagline,
        headline: portfolio.Headline,
        bio: portfolio.Bio,
        location: portfolio.Location,
        instagram: portfolio.Instagram,
        contact_email: portfolio.ContactEmail,
        cover_asset_id: portfolio.CoverAssetID || undefined,
        avatar_asset_id: portfolio.AvatarAssetID || undefined,
        logo_asset_id: portfolio.LogoAssetID || undefined,
        template_id: portfolio.TemplateID || undefined,
        accent_color: portfolio.AccentColor || undefined,
        background_color: portfolio.BackgroundColor || undefined,
        text_color: portfolio.TextColor || undefined,
        dark_primary: portfolio.DarkPrimary || undefined,
        dark_background: portfolio.DarkBackground || undefined,
        dark_text: portfolio.DarkText || undefined,
        font_family: portfolio.FontFamily || undefined,
        is_published: true,
        social_links: portfolio.SocialLinks?.map((s) => ({ url: s.url, label: s.label })),
      });
      setPublishedOk(true);
      setTimeout(() => setPublishedOk(false), 2500);
    } catch (err) {
      setPublishError(err instanceof Error ? err.message : "Failed to publish");
    } finally {
      setPublishing(false);
    }
  }

  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 200,
        height: 52,
        display: "flex",
        alignItems: "center",
        padding: "0 1rem",
        gap: "1rem",
        background: "var(--surface)",
        borderBottom: "1px solid var(--border)",
        flexShrink: 0,
      }}
    >
      {/* Hamburger — mobile only */}
      <button
        className="cms-menu-btn"
        onClick={onToggleMobile}
        style={{
          display: "none",
          alignItems: "center",
          justifyContent: "center",
          background: "transparent",
          border: "none",
          color: "var(--muted)",
          cursor: "pointer",
          padding: "0.3rem",
          borderRadius: 6,
          fontSize: "1.1rem",
          flexShrink: 0,
        }}
        aria-label="Open navigation"
      >
        ☰
      </button>

      {/* Back link */}
      <a
        href="/app/dashboard"
        onClick={(e) => { e.preventDefault(); navigate("/app/dashboard"); }}
        style={{
          fontSize: "0.875rem",
          color: "var(--muted)",
          textDecoration: "none",
          display: "flex",
          alignItems: "center",
          gap: "0.25rem",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        &#8592; Dashboard
      </a>

      {/* Center: page title */}
      <div
        style={{
          flex: 1,
          textAlign: "center",
          fontWeight: 600,
          fontSize: "0.95rem",
          color: "var(--text)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {pageTitle || "Website Settings"}
      </div>

      {/* Right: view site + publish */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0 }}>
        {portfolioUrl && (
          <a
            href={portfolioUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: "0.875rem",
              color: "var(--muted)",
              textDecoration: "none",
              whiteSpace: "nowrap",
            }}
          >
            View site &#8599;
          </a>
        )}
        {publishError && (
          <span style={{ fontSize: "0.8rem", color: "#ef4444", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {publishError}
          </span>
        )}
        <button
          onClick={handlePublish}
          disabled={publishing}
          style={{
            padding: "0.3rem 0.85rem",
            fontSize: "0.875rem",
            background: publishedOk ? "#10b981" : "var(--accent)",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            cursor: publishing ? "not-allowed" : "pointer",
            transition: "background 0.2s",
            fontWeight: 600,
          }}
        >
          {publishing ? "Publishing…" : publishedOk ? "Published!" : "Publish"}
        </button>
      </div>
    </header>
  );
}
